import { queryOptions } from '@tanstack/react-query'
import { contactApi, conversationApi, messageApi, whatsappTemplateApi } from './api'
import {
  ContactFilter,
  ConversationFilter,
  MessageFilter,
  WhatsappTemplateFilter,
  templateStatusEnum,
} from './models'
import { Pagination } from '@thinknimble/tn-models'

export const conversationQueries = {
  all: () => ['whatsapp-conversations'],
  retrieve: (id: string) => {
    return queryOptions({
      queryKey: [...conversationQueries.all(), id],
      queryFn: () => conversationApi.retrieve(id),
      enabled: Boolean(id),
    })
  },
  list: ({
    filters,
    pagination,
    paginationCallback,
  }: {
    filters?: Partial<ConversationFilter>
    pagination?: Pagination
    paginationCallback?: (pagination: Pagination) => void
  }) => {
    return queryOptions({
      queryKey: [...conversationQueries.all(), { filters, pagination }],
      queryFn: async () => {
        const res = await conversationApi.list({
          filters,
          pagination,
        })
        const serverPagination = new Pagination({ ...res, totalCount: res.count })
        paginationCallback?.(serverPagination)
        return res
      },
      enabled: true,
    })
  },
}

export const contactSearchQueries = {
  all: () => ['whatsapp-contact-search'],
  search: (query: string) => {
    return queryOptions({
      queryKey: [...contactSearchQueries.all(), query],
      queryFn: () => conversationApi.csc.contactSearch(query),
      enabled: query.trim().length >= 2,
    })
  },
}

export const messageQueries = {
  all: () => ['whatsapp-messages'],
  retrieve: (id: string) => {
    return queryOptions({
      queryKey: [...messageQueries.all(), id],
      queryFn: () => messageApi.retrieve(id),
      enabled: Boolean(id),
    })
  },
  list: ({
    filters,
    pagination,
    paginationCallback,
  }: {
    filters?: Partial<MessageFilter>
    pagination?: Pagination
    paginationCallback?: (pagination: Pagination) => void
  }) => {
    return queryOptions({
      queryKey: [...messageQueries.all(), { filters, pagination }],
      queryFn: async () => {
        const res = await messageApi.list({
          filters,
          pagination,
        })
        const serverPagination = new Pagination({ ...res, totalCount: res.count })
        paginationCallback?.(serverPagination)
        return res
      },
      enabled: Boolean(filters?.conversation),
    })
  },
}

export const contactQueries = {
  all: () => ['whatsapp-contacts'],
  list: ({
    filters,
    pagination,
  }: {
    filters?: Partial<ContactFilter>
    pagination?: Pagination
  } = {}) => {
    return queryOptions({
      queryKey: [...contactQueries.all(), { filters, pagination }],
      queryFn: () => contactApi.list({ filters, pagination }),
      enabled: true,
    })
  },
}

export const whatsappTemplateQueries = {
  all: () => ['whatsapp-templates'],
  list: ({
    filters,
    pagination,
  }: {
    filters?: Partial<WhatsappTemplateFilter>
    pagination?: Pagination
  } = {}) => {
    return queryOptions({
      queryKey: [...whatsappTemplateQueries.all(), { filters, pagination }],
      queryFn: () => whatsappTemplateApi.list({ filters, pagination }),
      enabled: true,
    })
  },
  /** The org's approved templates — what the hub is allowed to send. */
  approved: (organization: string | null | undefined) =>
    whatsappTemplateQueries.list({
      filters: {
        ...(organization ? { organization } : {}),
        status: templateStatusEnum.APPROVED,
      },
    }),
  /** Templates still awaiting Meta's review — drives the "in review" notice that
   * distinguishes a pending approval from having no template at all. */
  pending: (organization: string | null | undefined) =>
    whatsappTemplateQueries.list({
      filters: {
        ...(organization ? { organization } : {}),
        status: templateStatusEnum.PENDING,
      },
    }),
  /**
   * Every template for an org (all statuses) — the management page lists these
   * so admins see pending/rejected rows too, not just approved ones. Disabled
   * only when no org is selected yet.
   */
  byOrg: (
    organization: string | null | undefined,
    pagination?: Pagination,
    paginationCallback?: (pagination: Pagination) => void,
    includeDisabled = false,
  ) => {
    const filters = {
      ...(organization ? { organization } : {}),
      ...(includeDisabled ? { includeDisabled } : {}),
    }
    return queryOptions({
      queryKey: [...whatsappTemplateQueries.all(), { filters, pagination }],
      queryFn: async () => {
        const res = await whatsappTemplateApi.list({ filters, pagination })
        const serverPagination = new Pagination({ ...res, totalCount: res.count })
        paginationCallback?.(serverPagination)
        return res
      },
      enabled: Boolean(organization),
    })
  },
}
